import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Upload, RefreshCcw } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { listsAPI } from '../services/api.js';
import { formatPhone } from '../lib/format.js';

// Reused from CampaignDetail — same minimal CSV parser keeps everything
// client-side. The backend just receives a JSON array.
function parseCsv(text) {
  const rows = [];
  let cur = [], field = '', q = false;
  const push = () => { if (cur.length > 1 || (cur[0] && cur[0].length > 0)) rows.push(cur); cur = []; };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else q = false; }
      else field += c;
    } else {
      if (c === '"') q = true;
      else if (c === ',') { cur.push(field); field = ''; }
      else if (c === '\r') {}
      else if (c === '\n') { cur.push(field); field = ''; push(); }
      else field += c;
    }
  }
  if (field.length || cur.length) { cur.push(field); push(); }
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const KNOWN = {
    phone: 'phoneE164', phonee164: 'phoneE164', 'phone number': 'phoneE164', mobile: 'phoneE164',
    name: 'name', firstname: 'name', first_name: 'name', fullname: 'name',
    company: 'company', business: 'company', clinic: 'company',
    email: 'email'
  };
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.every((c) => !c.trim())) continue;
    const obj = {};
    const meta = {};
    for (let i = 0; i < headers.length; i++) {
      const h = headers[i], v = (row[i] || '').trim();
      if (!v) continue;
      const k = KNOWN[h];
      if (k) obj[k] = v; else meta[headers[i]] = v;
    }
    if (Object.keys(meta).length > 0) obj.metadata = meta;
    if (obj.phoneE164) out.push(obj);
  }
  return out;
}

export default function ListDetail() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [l, m] = await Promise.all([
        listsAPI.get(id),
        listsAPI.members(id, { limit: 200 })
      ]);
      setList(l?.data || l);
      setMembers(m?.data || []);
      setMemberTotal(m?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load list.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const onUpload = async (file) => {
    setUploadStatus('Parsing…');
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        setUploadStatus('No rows with a recognizable phone column found.');
        return;
      }
      setUploadStatus(`Uploading ${rows.length} rows…`);
      const r = await listsAPI.addMembers(id, rows);
      setUploadStatus(`Added ${r.data.added}, skipped ${r.data.skipped}, total in file ${r.data.total}.`);
      load();
    } catch (err) {
      setUploadStatus(err.message || 'Upload failed.');
    }
  };

  if (loading && !list) return <div className="px-6 md:px-10 py-8 text-stone-500">Loading…</div>;
  if (error && !list) return <div className="px-6 md:px-10 py-8"><ErrorBanner message={error} /></div>;
  if (!list) return <div className="px-6 md:px-10 py-8 text-stone-500">Not found.</div>;

  return (
    <div className="px-6 md:px-10 py-8 max-w-5xl">
      <Link to="/lists" className="inline-flex items-center gap-1 text-sm text-stone-400 hover:text-alfred-gold mb-4">
        <ChevronLeft size={16} /> Back to lists
      </Link>

      <PageHeader
        title={list.name}
        subtitle={`${list.memberCount.toLocaleString()} members${list.description ? ' · ' + list.description : ''}`}
        actions={
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            <RefreshCcw size={14} /> Refresh
          </button>
        }
      />

      {error && <div className="mb-6"><ErrorBanner message={error} /></div>}

      <section className="card mb-6">
        <h2 className="text-lg text-stone-100 mb-3">Add members</h2>
        <p className="text-sm text-stone-400 mb-3">
          CSV must have a header row. Recognized columns: <code>phone</code> (required, E.164),
          <code> name</code>, <code>company</code>, <code>email</code>. Anything else
          becomes metadata.
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
          className="block text-sm text-stone-300"
        />
        {uploadStatus && <p className="mt-3 text-xs text-stone-400">{uploadStatus}</p>}
      </section>

      <section className="card p-0 overflow-hidden">
        <header className="px-4 py-3 border-b border-stone-800 flex items-center justify-between">
          <h2 className="text-sm uppercase tracking-wider text-stone-500">
            Members ({memberTotal.toLocaleString()})
          </h2>
        </header>
        {members.length === 0 ? (
          <p className="p-6 text-sm text-stone-500">No members yet — upload a CSV above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-800">
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-stone-900">
                    <td className="px-3 py-2 text-stone-200 whitespace-nowrap">{formatPhone(m.phoneE164)}</td>
                    <td className="px-3 py-2 text-stone-300">{m.name || ''}</td>
                    <td className="px-3 py-2 text-stone-300">{m.company || ''}</td>
                    <td className="px-3 py-2 text-stone-400">{m.email || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
